import React, { useState, useEffect, useRef } from 'react';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  priority?: boolean; // For above-the-fold images that should load immediately
  loading?: 'lazy' | 'eager';
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
}

/**
 * OptimizedImage component that implements best practices for image loading:
 * - Lazy loading for below-the-fold images
 * - Width/height attributes to prevent layout shift
 * - Priority loading for critical images
 * - Proper error handling
 * - Intersection Observer for efficient lazy loading
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  priority = false,
  loading: loadingProp,
  className = '',
  onLoad,
  onError,
  placeholder = 'empty',
  blurDataURL,
  ...rest
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(priority); // Load immediately if priority
  const containerRef = useRef<HTMLDivElement>(null);

  // Determine loading strategy
  const loading = loadingProp || (priority ? 'eager' : 'lazy');

  useEffect(() => {
    // If priority, load immediately
    if (priority) {
      setShouldLoad(true);
      return;
    }

    // Use Intersection Observer for lazy loading
    if (!shouldLoad && containerRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setShouldLoad(true);
              observer.disconnect();
            }
          });
        },
        {
          rootMargin: '50px', // Start loading 50px before image enters viewport
        }
      );

      observer.observe(containerRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [priority, shouldLoad]);

  const handleLoad = () => {
    setImageLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setImageError(true);
    onError?.();
  };

  // Determine if this should be inline (for icons/logos in text)
  const isInline = className.includes('inline') || className.includes('inline-block');
  
  // Extract style prop from rest
  const { style: restStyle, ...restProps } = rest;

  // Use display: contents when wrapper isn't needed for layout
  // This makes the wrapper "transparent" to the layout system
  const needsWrapperLayout = !imageLoaded || imageError || !shouldLoad;

  return (
    <div
      ref={containerRef}
      className={needsWrapperLayout ? `relative ${isInline ? 'inline-block' : 'block'}` : ''}
      style={{
        display: needsWrapperLayout ? (isInline ? 'inline-block' : 'block') : 'contents',
        lineHeight: needsWrapperLayout ? 0 : 'normal',
      }}
    >
      {/* Placeholder/Blur */}
      {!imageLoaded && !imageError && placeholder === 'blur' && blurDataURL && (
        <div
          className="absolute inset-0 bg-muted animate-pulse"
          style={{
            backgroundImage: `url(${blurDataURL})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(20px)',
            transform: 'scale(1.1)',
          }}
        />
      )}

      {/* Skeleton placeholder */}
      {!imageLoaded && !imageError && placeholder === 'empty' && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}

      {/* Actual Image */}
      {shouldLoad && (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={loading}
          fetchpriority={priority ? 'high' : 'auto'}
          decoding="async"
          className={`transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          } ${className}`}
          style={{
            position: 'relative',
            zIndex: 1,
            ...restStyle,
          }}
          onLoad={handleLoad}
          onError={handleError}
          {...restProps}
        />
      )}

      {/* Error State */}
      {imageError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
          <div className="text-center p-4">
            <svg
              className="h-8 w-8 mx-auto text-muted-foreground mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-xs text-muted-foreground">Failed to load image</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
